import React, { useMemo, useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

// Liten, gjenbrukbar sorteringshook for tabeller.
//
// const { sorted, sortBy, dir, toggle, Th } = useSort(rows, {
//   defaultKey: 'dato', defaultDir: 'desc',
//   accessors: { dato: (r) => r.dato, belop: (r) => Number(r.belop_ink_mva || 0) },
// });
//
// I tabellen: <thead><tr>{Th('dato','Dato')}{Th('belop','Beløp', {align:'right'})}</tr></thead>
//
// "accessors" er valgfri — uten den brukes rad[nokkel] direkte.

const cmp = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;   // tomme nederst
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  // Forsøk numerisk
  const na = Number(a), nb = Number(b);
  if (!isNaN(na) && !isNaN(nb) && (typeof a !== 'string' || /^-?\d+([.,]\d+)?$/.test(a))) {
    return na - nb;
  }
  return String(a).localeCompare(String(b), 'nb', { numeric: true, sensitivity: 'base' });
};

export function useSort(rader, opts = {}) {
  const { defaultKey = null, defaultDir = 'asc', accessors = {} } = opts;
  const [sortBy, setSortBy] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);

  const toggle = useCallback((key) => {
    if (sortBy === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setDir('asc'); }
  }, [sortBy]);

  const sorted = useMemo(() => {
    if (!Array.isArray(rader)) return rader;
    if (!sortBy) return rader;
    const getter = accessors[sortBy] || ((r) => r?.[sortBy]);
    const kopi = [...rader];
    kopi.sort((a, b) => {
      const c = cmp(getter(a), getter(b));
      return dir === 'asc' ? c : -c;
    });
    return kopi;
  }, [rader, sortBy, dir, accessors]);

  const Th = useCallback((key, label, extra = {}) => {
    const { align, style, ...rest } = extra;
    const aktiv = sortBy === key;
    const Icon = !aktiv ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
    return (
      <th
        {...rest}
        onClick={() => toggle(key)}
        style={{
          cursor: 'pointer', userSelect: 'none',
          textAlign: align || undefined,
          ...style,
        }}
        title="Klikk for å sortere"
      >
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}>
          {label}
          <Icon size={11} style={{ opacity: aktiv ? 0.85 : 0.35 }} />
        </span>
      </th>
    );
  }, [sortBy, dir, toggle]);

  return { sorted, sortBy, dir, toggle, Th };
}
